<template>
  <teleport to="body">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
      <div class="w-full max-w-4xl bg-gray-800 text-indigo-200 rounded-md shadow-2xl border border-gray-700 overflow-hidden animate-scaleIn flex flex-col" style="max-height:60vh;">
        <!-- Sticky Header -->
        <header class="bg-gray-800 border-b border-gray-700">
          <div class="flex items-center justify-between p-4">
            <div class="flex items-center gap-3">
              <i class="fa-solid fa-tower-observation text-indigo-300 text-xl"></i>
              <div>
                <h2 class="text-base sm:text-lg font-semibold text-indigo-300">จัดการ Outpost {{ outpostId }}</h2>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button @click="emitClose" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group focus:outline-none p-2" aria-label="ปิด">
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
          </div>
        </header>

        <!-- Content -->
        <main class="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin" id="style-1">
          <template v-if="!isAssigning">
            <!-- Current Defenders -->
            <section class="space-y-4 animate-fadeIn">

              <button v-if="!defenderCount" disabled class="text-xs px-3 py-2 rounded-md bg-gray-700 border border-gray-600 transition cursor-not-allowed">ใช้ NPC เริ่มต้น (กำลังใช้งาน)</button>
              <button
                v-if="!outpost?.event"
                class="text-xs px-3 py-2 rounded-md bg-indigo-700 border border-indigo-600 hover:bg-indigo-600 transition flex items-center justify-center w-full"
                :disabled="assigningStarting || (defenderCount >= maxDefenders)"
                @click="startAssign"
              >
                <span v-if="!assigningStarting">เพิ่มชาวบ้านป้องกัน</span>
                <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
              </button>
              <div v-if="outpost?.event" class="text-xs text-center text-red-400 bg-red-900 bg-opacity-30 border border-red-700 rounded px-3 py-2">
                <i class="fas fa-exclamation-circle mr-1"></i>ไม่สามารถส่งชาวบ้านป้องกันได้เพราะ outpost กำลังถูกโจมตี
              </div>

              <!-- Treasure Box Section -->
              <div v-if="isOwnOutpost" class="relative">
                <!-- Primary action row -->
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div class="flex items-center gap-2">
                    <button
                      v-if="!isOpeningTreasure"
                      @click="confirmOpen = true"
                      :disabled="!isTreasureAvailable"
                      class="group inline-flex items-center gap-3 px-4 py-2 rounded-md transition-colors duration-200 focus:outline-none border"
                      :class="[
                        !isTreasureAvailable
                          ? 'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed'
                          : 'bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-500',
                      ]"
                      aria-label="เปิดกล่องสมบัติ"
                      @mouseenter="showHintOpen = true"
                      @mouseleave="showHintOpen = false"
                    >
                      <span class="relative inline-flex">
                        <i
                          class="fas fa-treasure-chest text-lg"
                          :class="!isTreasureAvailable ? 'text-yellow-300 opacity-60' : 'text-yellow-300'"
                        ></i>
                      </span>
                      <div class="flex flex-col items-start leading-tight w-full">
                        <span class="font-semibold text-sm">เปิดกล่องสมบัติ</span>
                        <span class="text-xxs opacity-80" v-if="isTreasureAvailable">รับรางวัลยึดครองประจำวัน</span>
                        <span class="text-xxs opacity-80" v-else-if="treasureOpened">ถูกเปิดแล้ววันนี้</span>
                        <span class="text-xxs opacity-80" v-else-if="remainingTreasureMs > 0">คูลดาวน์เหลือ {{ formatRemaining(remainingTreasureMs) }}</span>
                      </div>
                    </button>

                    <!-- Loading state -->
                    <div
                      v-else
                      class="inline-flex items-center gap-3 px-4 py-2 rounded-md bg-indigo-600 text-white border border-indigo-500"
                    >
                      <i class="fas fa-treasure-chest animate-shake text-yellow-300 text-xl"></i>
                      <span class="animate-pulse text-sm">กำลังเปิดกล่องสมบัติ...</span>
                    </div>

                    <!-- Tiny hover hint -->
                    <div
                      v-if="showHintOpen && isTreasureAvailable && !isOpeningTreasure"
                      class="hidden sm:block text-xxs bg-gray-900 border border-gray-700 px-2 py-1 rounded-md"
                    >
                      เปิดเพื่อรับเพชรและสุ่มไอเท็ม
                    </div>
                  </div>

                  <!-- Right info side -->
                  <div class="text-xxs sm:text-xs text-indigo-300 space-y-1">
                    <p v-if="treasureOpened">{{ countdownText }}</p>
                    <p v-else-if="remainingTreasureMs > 0">คูลดาวน์เหลือ {{ formatRemaining(remainingTreasureMs) }}</p>
                    <p v-if="treasureMessage" :class="treasureMessage.includes('สำเร็จ') ? 'text-green-400' : 'text-red-400'">
                      {{ treasureMessage }}
                    </p>
                  </div>
                </div>

                <!-- Confirm panel -->
                <transition name="fade">
                  <div
                    v-if="confirmOpen && isTreasureAvailable"
                    class="mt-2 p-3 rounded-md border bg-gray-900 border-gray-700"
                  >
                    <div class="flex items-start gap-3">
                      <i class="fas fa-exclamation-triangle text-yellow-400 mt-0.5"></i>
                      <div class="flex-1">
                        <p class="text-sm text-indigo-200 font-medium">ยืนยันการเปิดกล่องสมบัติ</p>
                        <p class="text-xxs text-indigo-300">สามารถเปิดได้วันละครั้ง รีเซ็ตเวลา 21:00 น. (เวลาไทย)</p>
                      </div>
                    </div>
                    <div class="mt-3 flex items-center gap-2">
                      <button
                        @click="openTreasureBox"
                        class="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-xs border border-indigo-500"
                      >
                        ยืนยันเปิด
                      </button>
                      <button
                        @click="confirmOpen = false"
                        class="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-indigo-200 text-xs border border-gray-600"
                      >
                        ยกเลิก
                      </button>
                    </div>
                  </div>
                </transition>
              </div>

              <div v-if="!defenderCount" class="mt-4">
                <button
                  v-if="!resigning"
                  @click="confirmResign = true"
                  class="text-xs px-3 py-2 rounded-md bg-red-700 border border-red-600 hover:bg-red-600 transition flex items-center justify-center w-full"
                >
                  ถอนตัวจากการยึดครอง
                </button>
                <div v-else class="flex items-center justify-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                </div>
              </div>

              <p v-else class="text-xs px-3 py-2 rounded-md bg-red-900 border border-red-600 transition flex items-center justify-center w-full">
                ต้องเรียกชาวบ้านป้องกันกลับหมดก่อนจึงจะถอนตัวได้
              </p>

              <p v-if="resignMessage" class="text-xs" :class="resignMessage.includes('สำเร็จ') ? 'text-green-400' : 'text-red-400'">
                {{ resignMessage }}
              </p>

              <!-- Confirm Resign Panel -->
              <transition name="fade">
                <div v-if="confirmResign" class="mt-2 p-3 rounded-md border bg-gray-900 border-gray-700">
                  <div class="flex items-start gap-3">
                    <i class="fas fa-exclamation-triangle text-yellow-400 mt-0.5"></i>
                    <div class="flex-1">
                      <p class="text-sm text-indigo-200 font-medium">ยืนยันการถอนตัวจากการยึดครอง</p>
                      <p class="text-xxs text-indigo-300">คุณจะสูญเสียสิทธิ์ในการรับรางวัลรายวันจาก outpost นี้</p>
                    </div>
                  </div>
                  <div class="mt-3 flex items-center gap-2">
                    <button
                      @click="resignOutpost"
                      class="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-500 text-white text-xs border border-red-500"
                    >
                      ยืนยันถอนตัว
                    </button>
                    <button
                      @click="confirmResign = false"
                      class="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-indigo-200 text-xs border border-gray-600"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </div>
              </transition>

              <div class="bg-gray-900 bg-opacity-60 border border-gray-700 rounded-md p-3">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="text-xs font-semibold text-indigo-300">{{ defendersTabName }}</h3>
                  <div class="text-xxs text-gray-400">{{ defendersData.length }} ตัว</div>
                </div>

                <div v-if="defendersData.length" class="grid grid-cols-1 gap-4">
                  <div
                    v-for="(def, idx) in defendersData"
                    :key="(def.id || def.name || 'def-' + idx)"
                    class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition"
                  >
                    <!-- ส่วนหัว: ภาพ + ชื่อ + เลเวล -->
                    <div class="p-3 flex items-center">
                      <div class="relative">
                        <img
                          :src="def.img || def.template?.normalFace || def.normalFace || 'https://via.placeholder.com/64?text=No+Img'"
                          :alt="def.name"
                          class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                          @error="handleImageError"
                        />
                        <div
                          v-if="def.level || def.template?.level"
                          class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                        >
                          {{ def.level || def.template?.level }}
                        </div>
                      </div>

                      <div class="ml-3 flex-1">
                        <div class="flex items-center justify-between">
                          <div class="font-semibold text-gray-100 truncate">
                            {{ def.name }}

                            <button
                              v-if="def.template"
                              @click="openUpgradeModal(def)"
                              class="ml-1 text-green-300 hover:text-green-100"
                              title="อัปเกรด"
                            >
                              <i class="fas fa-plus"></i>
                            </button>

                            <button @click="openPowerModal(def)" class="ml-1 text-indigo-300 hover:text-indigo-100">
                              <i class="fa-solid fa-circle-info"></i>
                            </button>
                          </div>
                        </div>

                        <div class="flex items-center space-x-1 px-1">
                          <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                            <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                            <span class="font-mono">{{ getDice(def, 'defenseDice') || 6 }}</span>
                          </div>
                          <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                            <i class="fa-solid fa-dice text-red-300 text-xs"></i>
                            <span class="font-mono">{{ getDice(def, 'attackDice') || 6 }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- แท็บสเตตัสแบบย่อ -->
                    <div class="px-3 pb-3 space-y-2">
                      <div class="grid grid-cols-3 gap-1">
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          sta: {{ getTotalStat(def, 'stamina') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          str: {{ getTotalStat(def, 'str') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          agi: {{ getTotalStat(def, 'agi') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          int: {{ getTotalStat(def, 'int') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          luck: {{ getTotalStat(def, 'luck') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          def: {{ getTotalStat(def, 'defense') }}
                        </span>
                      </div>
                    </div>

                    <!-- Attack Readiness Status เฉพาะชาวบ้านจริง ไม่แสดงสำหรับ NPC -->
                    <div class="px-3 pb-3 flex items-center flex-wrap gap-2" v-if="def.template">
                      <div v-if="remainingMs(def) > 0" class="flex items-center text-yellow-400 text-xxs">
                        <i class="fas fa-hourglass-half mr-1 animate-pulse"></i>
                        <span class="font-medium">สามารถบุกได้หลัง</span> {{ formatRemaining(remainingMs(def)) }}
                      </div>
                      <div v-else class="flex items-center text-green-400 text-xxs">
                        <i class="fas fa-check-circle mr-1"></i>
                        <span class="font-medium">พร้อมใช้งาน</span>
                      </div>
                      <button
                        v-if="isAdmin && remainingMs(def) > 0"
                        @click="resetCooldown(def.id)"
                        class="text-xs bg-purple-600 hover:bg-purple-500 text-white px-2 py-0.5 rounded transition"
                      >
                        Reset
                      </button>
                    </div>

                    <!-- Recall and Reset Buttons เฉพาะชาวบ้านจริง -->
                    <div class="px-3 pb-3 flex gap-2">
                      <button
                        v-if="def.template && canRecall(def)"
                        @click="recallVillager(def.id)"
                        :disabled="recalling[def.id]"
                        class="flex-1 px-2 py-1 text-white rounded text-xs bg-red-700 hover:bg-red-600 transition flex items-center justify-center"
                      >
                        <span v-if="!recalling[def.id]">เรียกกลับ</span>
                        <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                      </button>

                      <button
                        v-if="isAdmin && def.template && !canRecall(def)"
                        @click="resetRecallTime(def.id)"
                        :disabled="resetting[def.id]"
                        class="flex-1 px-2 py-1 text-white rounded text-xs bg-purple-700 hover:bg-purple-600 transition flex items-center justify-center"
                      >
                        <span v-if="!resetting[def.id]">รีเซ็ตเวลา</span>
                        <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                      </button>
                    </div>

                    <div v-if="def.template && !canRecall(def)" class="px-3 pb-3 text-xxs text-gray-400">
                      ไม่สามารถเรียกกลับได้เพราะ outpost กำลังถูกโจมตี
                    </div>

                    <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
                  </div>
                </div>

                <p v-else class="text-indigo-300 text-xs text-center py-6">—</p>
              </div>
            </section>
          </template>

          <template v-else>
            <!-- Villager Assignment Selection -->
            <section class="space-y-4 animate-fadeIn">
              <h3 class="text-sm font-semibold text-indigo-300">
                เลือกชาวบ้านสำหรับป้องกัน (สูงสุด {{ maxAssignable }} คน) - เลือกแล้ว {{ selectedVillagerIds.length }} คน
              </h3>

              <div v-if="playerVillagers.length > 0" class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div
                  v-for="v in sortedPlayerVillagers"
                  :key="v.id"
                  class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition"
                >
                  <!-- ส่วนหัว: ภาพ + ชื่อ + เลเวล -->
                  <div class="p-3 flex items-center">
                    <div class="relative">
                      <img
                        :src="v.img || v.template?.normalFace || 'https://via.placeholder.com/64?text=No+Img'"
                        :alt="v.name"
                        class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                        @error="handleImageError"
                      />
                      <div
                        v-if="v.level || v.template?.level"
                        class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                      >
                        {{ v.level ?? v.template?.level }}
                      </div>
                    </div>

                    <div class="ml-3 flex-1">
                      <div class="flex items-center justify-between">
                        <div class="font-semibold text-gray-100 truncate">
                          {{ v.name }}
                          <button @click="openPowerModal(v)" class="ml-1 text-indigo-300 hover:text-indigo-100">
                            <i class="fa-solid fa-circle-info"></i>
                          </button>
                          <button @click="openUpgradeModal(v)" class="ml-1 text-indigo-300 hover:text-indigo-100" title="อัปเกรด">
                            <i class="fas fa-plus"></i>
                          </button>
                        </div>
                      </div>

                      <div class="flex items-center space-x-1 px-1">
                        <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                          <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                          <span class="font-mono">{{ getDice(v, 'defenseDice') || 6 }}</span>
                        </div>
                        <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                          <i class="fa-solid fa-dice text-red-300 text-xs"></i>
                          <span class="font-mono">{{ getDice(v, 'attackDice') || 6 }}</span>
                        </div>
                      </div>

                      <div class="text-xs text-gray-400 truncate" v-if="v.template?.note">
                        {{ v.template.note }}
                      </div>
                    </div>
                  </div>

                  <!-- แท็บสเตตัสแบบย่อ -->
                  <div class="px-3 pb-3 space-y-2">
                    <div class="grid grid-cols-3 gap-1">
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        sta: {{ getTotalStat(v, 'stamina') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        str: {{ getTotalStat(v, 'str') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        agi: {{ getTotalStat(v, 'agi') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        int: {{ getTotalStat(v, 'int') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        luck: {{ getTotalStat(v, 'luck') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        def: {{ getTotalStat(v, 'defense') }}
                      </span>
                    </div>
                  </div>

                  <!-- Selection Button -->
                  <div class="px-3 pb-3">
                    <button
                      @click="toggleSelect(v.id)"
                      class="w-full px-2 py-1 text-white rounded text-xs transition"
                      :class="[
                        selectedVillagerIds.includes(v.id)
                          ? 'bg-green-700 hover:bg-green-600'
                          : isSelectable(v)
                            ? 'bg-indigo-700 hover:bg-indigo-600'
                            : 'bg-gray-700 cursor-not-allowed'
                      ]"
                      :disabled="!selectedVillagerIds.includes(v.id) && !isSelectable(v)"
                    >
                      {{ selectedVillagerIds.includes(v.id) ? 'ยกเลิก' : (isSelectable(v) ? 'เลือก' : 'เลือกไม่ได้') }}
                    </button>
                  </div>

                  <div v-if="!v.canAssign" class="px-3 pb-3 text-xxs text-red-400">
                    ชาวบ้านไม่คุ้นเคยกับสถานที่นี้
                  </div>

                  <div v-if="isNameInCurrentDefenders(v.name)" class="px-3 pb-3 text-xxs text-red-400">
                    มีชาวบ้านชื่อนี้อยู่ในป้อมแล้ว
                  </div>

                  <div v-if="isNameAlreadySelected(v.name) && !selectedVillagerIds.includes(v.id)" class="px-3 pb-3 text-xxs text-red-400">
                    ไม่สามารถเลือกชาวบ้านชื่อซ้ำกันได้
                  </div>

                  <div v-if="selectedVillagerIds.length >= maxAssignable && !selectedVillagerIds.includes(v.id)" class="px-3 pb-3 text-xxs text-red-400">
                    ถึงขีดจำกัดจำนวนชาวบ้านแล้ว
                  </div>

                  <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
                </div>
              </div>

              <p v-else class="text-indigo-300 text-xs text-center py-6">คุณไม่มีชาวบ้านสำหรับป้องกัน</p>
            </section>
          </template>
        </main>

        <!-- Footer -->
        <footer class="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-3 flex items-center justify-end gap-2">
          <template v-if="!isAssigning">
            <button class="text-xs px-3 py-2 rounded-md bg-gray-700 border border-gray-600 hover:bg-gray-600 transition" @click="emitClose">ปิด</button>
          </template>

          <template v-else>
            <button class="text-xs px-3 py-2 rounded-md bg-gray-700 border border-gray-600 hover:bg-gray-600 transition" @click="cancelAssign">ยกเลิก</button>
            <button
              class="text-xs px-3 py-2 rounded-md bg-indigo-700 border border-indigo-600 hover:bg-indigo-600 transition flex items-center justify-center"
              :disabled="selectedVillagerIds.length === 0 || assigningLoading"
              @click="assignVillagers"
            >
              <span v-if="!assigningLoading">ส่งชาวบ้านไปป้องกัน</span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </template>
        </footer>

        <!-- Villager Power Modal -->
        <VillagerPowerModal
          v-if="showPowerModal"
          :villager="selectedVillager"
          @close="showPowerModal = false"
        />

        <!-- Upgrade Villager Modal -->
        <UpgradeVillagerModal
          v-if="showUpgradeModal"
          :villager="selectedVillager"
          @close="showUpgradeModal = false"
          @updated="handleUpdated"
        />
      </div>
    </div>
  </teleport>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import { projectFirestore, projectFunctions } from '@/firebase/config'
import getUser from '@/composables/getUser'
import { usePlayerStore } from '@/composables/usePlayerStore'
import VillagerPowerModal from '@/components/villager/VillagerPowerModal.vue'
import UpgradeVillagerModal from '@/components/villager/UpgradeVillagerModal.vue'
import gameVillagersData from '@/data/gameVillagers.json'

export default {
  name: 'ManageOutpostModal',
  props: {
    outpostId: { type: String, required: true }
  },
  emits: ['close'],
  components: {
    VillagerPowerModal,
    UpgradeVillagerModal
  },
  setup (props, { emit }) {
    const { user } = getUser()
    const outpost = ref(null)
    const outpostPrivate = ref(null)

    let stop = null
    let stopPrivate = null
    let prevBodyOverflow = ''
    let interval = null

    // Admin check
    const playerStore = usePlayerStore()
    const isAdmin = ref(false)
    watch(() => playerStore.userData, (data) => {
      isAdmin.value = data?.admin === true
    }, { immediate: true })

    // Defenders data
    const defendersData = ref([])
    const villagerDetails = ref({})
    const templateCache = ref({})
    const isLoadingVillager = ref({})

    // Assignment system
    const isAssigning = ref(false)
    const selectedVillagerIds = ref([])
    const playerVillagers = ref([])

    // Loading state for buttons
    const recalling = ref({})
    const resetting = ref({})
    const assigningLoading = ref(false)
    const assigningStarting = ref(false)

    // Resign states
    const confirmResign = ref(false)
    const resigning = ref(false)
    const resignMessage = ref('')

    // Countdown
    const currentTime = ref(Date.now())

    // Treasure box states
    const treasureMessage = ref('')
    const isOpeningTreasure = ref(false)
    const confirmOpen = ref(false)
    const showHintOpen = ref(false)
    const countdownText = ref('')

    const isPlainObject = (value) => {
      return value !== null && typeof value === 'object' && !Array.isArray(value)
    }

    const getOutpostDefendersRaw = () => {
      return outpost.value?.defenders
    }

    const getPlayerDefendersMap = () => {
      const publicDefenders = getOutpostDefendersRaw()

      // รูปแบบใหม่ของคุณ: outposts/{outpostId}.defenders เป็น map
      if (isPlainObject(publicDefenders)) {
        return publicDefenders
      }

      // สำรองไว้เผื่อบาง outpost ยังเก็บใน outpostPrivate
      const privateDefenders = outpostPrivate.value?.defenders
      if (isPlainObject(privateDefenders)) {
        return privateDefenders
      }

      return {}
    }

    const getNpcDefenderNames = () => {
      const publicDefenders = getOutpostDefendersRaw()

      // รูปแบบเก่า/NPC: outposts/{outpostId}.defenders เป็น array เช่น ['ปรีดี', 'จัญญา']
      if (Array.isArray(publicDefenders)) {
        return publicDefenders
      }

      return []
    }

    const getLegacyDefenderIds = () => {
      if (Array.isArray(outpostPrivate.value?.defenderVillagerIds)) {
        return outpostPrivate.value.defenderVillagerIds
      }

      return []
    }

    const maxDefenders = computed(() => outpost.value?.maxDefenderVillagers || 5)

    const defenderCount = computed(() => {
      const playerCount = Object.keys(getPlayerDefendersMap()).length
      const legacyCount = getLegacyDefenderIds().length

      // สำคัญ: defenderCount ใช้นับเฉพาะชาวบ้านจริงที่ผู้เล่นส่งไป
      // ถ้าเป็น NPC array จะนับเป็น 0 เพื่อให้ปุ่ม/ข้อความเข้าเงื่อนไข "ใช้ NPC เริ่มต้น"
      return playerCount + legacyCount
    })

    const maxAssignable = computed(() => {
      return Math.max(0, maxDefenders.value - defenderCount.value)
    })

    const defendersTabName = computed(() => {
      return defenderCount.value ? 'รายชื่อชาวบ้าน' : 'NPC ป้องกัน'
    })

    const isOwnOutpost = computed(() => outpost.value?.ownerUid === user.value?.uid)

    const treasureOpened = computed(() => outpost.value?.treasureOpened || false)

    const remainingTreasureMs = computed(() => {
      if (!outpost.value?.treasureAvailableAt) return 0
      const available = outpost.value.treasureAvailableAt.toDate().getTime()
      return Math.max(0, available - currentTime.value)
    })

    const isTreasureAvailable = computed(() => !treasureOpened.value && remainingTreasureMs.value <= 0)

    const currentDefenderNames = computed(() => {
      if (!defenderCount.value) return []
      return defendersData.value.map(def => def.name)
    })

    const updateCountdown = () => {
      const now = new Date(currentTime.value)
      const resetTime = new Date(now)

      resetTime.setHours(21, 0, 0, 0)

      if (now > resetTime) {
        resetTime.setDate(resetTime.getDate() + 1)
      }

      const diff = resetTime - now

      if (diff > 0) {
        const hours = Math.floor(diff / 3600000)
        const minutes = Math.floor((diff % 3600000) / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)

        countdownText.value = `รีเซ็ตใน ${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      } else {
        countdownText.value = 'รีเซ็ตแล้ว - สามารถเปิดได้'
      }
    }

    const openTreasureBox = async () => {
      confirmOpen.value = false
      isOpeningTreasure.value = true
      treasureMessage.value = ''

      try {
        const { data } = await projectFunctions.httpsCallable('gameActions')({
          action: 'outpostActions',
          subAction: 'openTreasureBox',
          outpostId: props.outpostId
        })

        if (data.success) {
          treasureMessage.value = data.message
        } else {
          treasureMessage.value = data.error || 'เกิดข้อผิดพลาด'
        }
      } catch (e) {
        treasureMessage.value = e.message || 'เกิดข้อผิดพลาดในการเปิดกล่อง'
      } finally {
        isOpeningTreasure.value = false
      }
    }

    const formatRemaining = (ms) => {
      if (ms <= 0) return '00:00:00'

      const hours = Math.floor(ms / 3600000)
      const minutes = Math.floor((ms % 3600000) / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    const remainingMs = (def) => {
      if (!def.lastUsedInAttackAt) return 0

      const last = def.lastUsedInAttackAt.toDate().getTime()
      const cooldown = 20 * 60 * 60 * 1000 // 20 ชั่วโมง
      const elapsed = currentTime.value - last

      return Math.max(0, cooldown - elapsed)
    }

    const resetCooldown = async (id) => {
      if (!isAdmin.value || !user.value?.uid) return

      try {
        await projectFirestore.collection('camps').doc(user.value.uid).update({
          [`villagers.${id}.states.lastUsedInAttackAt`]: null
        })
      } catch (error) {
        console.error('Error resetting attack cooldown:', error)
      }
    }

    const subscribe = (id) => {
      if (stop) stop()
      outpost.value = null
      defendersData.value = []

      const docRef = projectFirestore.collection('outposts').doc(id)

      stop = docRef.onSnapshot(
        async snap => {
          if (!snap.exists) {
            outpost.value = null
            defendersData.value = []
            return
          }

          outpost.value = snap.data()
          await loadDefenders('outpost snapshot')
        },
        err => {
          console.error(err)
        }
      )
    }

    const subscribePrivate = (id) => {
      if (stopPrivate) stopPrivate()
      outpostPrivate.value = null

      const docRef = projectFirestore.collection('outpostPrivate').doc(id)

      stopPrivate = docRef.onSnapshot(
        async snap => {
          if (!snap.exists) {
            outpostPrivate.value = null
            await loadDefenders('private missing')
            return
          }

          outpostPrivate.value = snap.data()
          await loadDefenders('private snapshot')
        },
        err => {
          console.error(err)
        }
      )
    }

    const shouldSubscribePrivate = () => {
      if (!outpost.value || !user.value?.uid) return false
      return outpost.value.ownerUid === user.value.uid || isAdmin.value
    }

    const emitClose = () => {
      emit('close')
    }

    const onKey = (e) => {
      if (e.key === 'Escape') emitClose()
    }

    const fetchVillagerDetails = async (npcName) => {
      if (!npcName) return
      if (villagerDetails.value[npcName] || isLoadingVillager.value[npcName]) return

      isLoadingVillager.value[npcName] = true

      try {
        const doc = await projectFirestore.collection('gameVillagers').doc(npcName).get()
        villagerDetails.value[npcName] = doc.exists ? doc.data() : null
      } catch (e) {
        console.error('fetch npc villager failed:', npcName, e)
        villagerDetails.value[npcName] = null
      } finally {
        isLoadingVillager.value[npcName] = false
      }
    }

    const fetchTemplate = async (name) => {
      if (templateCache.value[name]) return templateCache.value[name]

      const t = gameVillagersData?.[name] || null
      templateCache.value[name] = t

      return t
    }

    const normalizePlayerDefender = (id, rawDefender) => {
      const rawStates = rawDefender?.states || {}

      // รองรับทั้ง 2 กรณี:
      // 1. { templateId: 'นที', states: {...} }
      // 2. { states: { templateId: 'นที', level: 1, ... } } แบบที่คุณมีตอนนี้
      const templateId = rawDefender?.templateId || rawStates?.templateId || id
      const template = gameVillagersData?.[templateId] || {}

      return {
        id,
        name: template?.name || templateId,
        templateId,

        // ให้ข้อมูล template อยู่ด้านนอกด้วย เพื่อ img/stats/dice ใช้งานง่าย
        ...template,

        // states ต้อง spread หลัง template เพื่อให้ level, assignedAt, lastUsedInAttackAt ทับค่าจาก template ได้
        ...rawStates,

        template,

        stats: template?.stats || rawDefender?.stats || {},
        upgradeStats: rawStates?.upgradeStats || rawDefender?.upgradeStats || {},

        assignedAt: rawStates?.assignedAt || rawDefender?.assignedAt || null,
        createdAt: rawStates?.createdAt || rawDefender?.createdAt || null,
        lastUsedInAttackAt: rawStates?.lastUsedInAttackAt || rawDefender?.lastUsedInAttackAt || null
      }
    }

    const loadDefenders = async (source = '') => {
      const playerDefendersMap = getPlayerDefendersMap()
      const playerEntries = Object.entries(playerDefendersMap)
      const legacyIds = getLegacyDefenderIds()

      defendersData.value = []

      console.log('[loadDefenders]', {
        source,
        outpostId: props.outpostId,
        publicDefendersType: Array.isArray(outpost.value?.defenders) ? 'array npc' : typeof outpost.value?.defenders,
        playerDefenderCount: playerEntries.length,
        legacyCount: legacyIds.length
      })

      // ถ้ามีชาวบ้านจริงที่ผู้เล่นส่งไปป้องกัน ไม่ต้องแสดง NPC
      if (playerEntries.length > 0) {
        defendersData.value = playerEntries.map(([id, rawDefender]) => {
          return normalizePlayerDefender(id, rawDefender)
        })

        return
      }

      // เผื่อของเก่า
      if (legacyIds.length > 0) {
        defendersData.value = legacyIds.map(id => ({
          id,
          name: 'ชาวบ้านแบบเก่า (ต้อง Migrate)',
          isLegacy: true,
          template: null,
          upgradeStats: {}
        }))

        return
      }

      // ถ้าไม่มีชาวบ้านจริง จึงแสดง NPC จาก outposts.defenders ที่เป็น array
      const npcNames = getNpcDefenderNames()

      if (npcNames.length > 0) {
        await Promise.all(npcNames.map(fetchVillagerDetails))

        defendersData.value = npcNames.map(npc => ({
          id: npc,
          name: npc,
          ...(villagerDetails.value[npc] || {}),
          template: null,
          upgradeStats: {}
        }))

        return
      }

      defendersData.value = []
    }

    const handleImageError = (e) => {
      e.target.src = 'https://via.placeholder.com/64?text=No+Img'
    }

    const getTotalStat = (v, key) => {
      const base = v.stats?.[key] || v.template?.stats?.[key] || 0
      const upgrade = v.upgradeStats?.[key] || 0

      return base + upgrade
    }

    const getDice = (v, key) => {
      return v[key] || v.template?.[key] || 0
    }

    const canRecall = () => {
      return !outpost.value?.event
    }

    const recallVillager = async (villagerId) => {
      recalling.value[villagerId] = true

      try {
        const { data } = await projectFunctions.httpsCallable('gameActions')({
          action: 'outpostActions',
          subAction: 'recallVillager',
          outpostId: props.outpostId,
          villagerId
        })

        if (data.success) {
          console.log(data.message)
        } else {
          console.error(data.error)
        }
      } catch (e) {
        console.error('Error recalling villager:', e)
      } finally {
        recalling.value[villagerId] = false
      }
    }

    const resetRecallTime = async (villagerId) => {
      if (!isAdmin.value || !user.value?.uid) return

      resetting.value[villagerId] = true

      try {
        await projectFirestore.collection('camps').doc(user.value.uid).update({
          [`villagers.${villagerId}.states.assignedAt`]: null
        })

        console.log('รีเซ็ตเวลาเรียกกลับสำเร็จ')
      } catch (e) {
        console.error('Error resetting recall time:', e)
      } finally {
        resetting.value[villagerId] = false
      }
    }

    // Check for duplicate names
    const isNameAlreadySelected = (name) => {
      return selectedVillagerIds.value.some(id => {
        const villager = playerVillagers.value.find(v => v.id === id)
        return villager && villager.name === name
      })
    }

    const isNameInCurrentDefenders = (name) => {
      return currentDefenderNames.value.includes(name)
    }

    const isSelectable = (v) => {
      const selected = selectedVillagerIds.value.includes(v.id)

      if (selected) return true

      const nameSelected = isNameAlreadySelected(v.name)
      const nameInCurrent = isNameInCurrentDefenders(v.name)

      return v.canAssign && !nameSelected && !nameInCurrent && selectedVillagerIds.value.length < maxAssignable.value
    }

    const sortedPlayerVillagers = computed(() => {
      const selectable = playerVillagers.value.filter(v => isSelectable(v))
      const nonSelectable = playerVillagers.value.filter(v => !isSelectable(v))

      const sortFn = (a, b) => {
        const levelA = a.level ?? a.template?.level ?? 0
        const levelB = b.level ?? b.template?.level ?? 0

        if (levelA !== levelB) return levelB - levelA

        const diceA = getDice(a, 'defenseDice')
        const diceB = getDice(b, 'defenseDice')

        return diceB - diceA
      }

      return [...selectable.sort(sortFn), ...nonSelectable.sort(sortFn)]
    })

    const fetchPlayerVillagers = async () => {
      if (!user.value?.uid) return

      try {
        const campSnap = await projectFirestore.collection('camps').doc(user.value.uid).get()
        if (!campSnap.exists) return

        const villagersObj = campSnap.data().villagers || {}
        const outpostLocation = outpost.value?.location

        playerVillagers.value = Object.entries(villagersObj).map(([id, v]) => {
          const states = v.states || {}
          const templateId = v.templateId || states.templateId
          const template = gameVillagersData?.[templateId] || {}
          const locs = template?.locations || []
          const canAssign = locs.length === 0 || locs.includes(outpostLocation)

          return {
            id,
            name: template?.name || templateId || id,
            templateId,
            ...template,
            ...states,
            template,
            upgradeStats: states?.upgradeStats || {},
            canAssign
          }
        })
      } catch (e) {
        console.error('Error fetching villagers for assign:', e)
      }
    }

    const startAssign = async () => {
      assigningStarting.value = true
      isAssigning.value = true
      selectedVillagerIds.value = []

      await fetchPlayerVillagers()

      assigningStarting.value = false
    }

    const cancelAssign = () => {
      isAssigning.value = false
      selectedVillagerIds.value = []
      playerVillagers.value = []
    }

    const toggleSelect = (vid) => {
      const v = playerVillagers.value.find(v => v.id === vid)
      if (!v) return

      if (selectedVillagerIds.value.includes(vid)) {
        selectedVillagerIds.value = selectedVillagerIds.value.filter(id => id !== vid)
      } else {
        if (!isSelectable(v)) return
        selectedVillagerIds.value.push(vid)
      }
    }

    const assignVillagers = async () => {
      assigningLoading.value = true

      try {
        const { data } = await projectFunctions.httpsCallable('gameActions')({
          action: 'outpostActions',
          subAction: 'assignVillagers',
          outpostId: props.outpostId,
          villagerIds: selectedVillagerIds.value
        })

        if (data.success) {
          console.log(data.message)
          cancelAssign()
        } else {
          console.error(data.error)
        }
      } catch (e) {
        console.error('Error assigning villagers:', e)
      } finally {
        assigningLoading.value = false
      }
    }

    const resignOutpost = async () => {
      confirmResign.value = false
      resigning.value = true
      resignMessage.value = ''

      try {
        const { data } = await projectFunctions.httpsCallable('gameActions')({
          action: 'outpostActions',
          subAction: 'resignOutpost',
          outpostId: props.outpostId
        })

        if (data.success) {
          resignMessage.value = data.message
          emitClose()
        } else {
          resignMessage.value = data.error || 'เกิดข้อผิดพลาด'
        }
      } catch (e) {
        resignMessage.value = e.message || 'เกิดข้อผิดพลาดในการถอนตัว'
      } finally {
        resigning.value = false
      }
    }

    // Villager modal functions
    const selectedVillager = ref(null)
    const showPowerModal = ref(false)
    const showUpgradeModal = ref(false)

    const openPowerModal = (v) => {
      selectedVillager.value = v
      showPowerModal.value = true
    }

    const openUpgradeModal = (v) => {
      selectedVillager.value = v
      showUpgradeModal.value = true
    }

    const handleUpdated = async () => {
      showUpgradeModal.value = false

      await loadDefenders('updated')

      if (isAssigning.value) {
        await fetchPlayerVillagers()
      }
    }

    onMounted(() => {
      subscribe(props.outpostId)

      document.addEventListener('keydown', onKey)
      prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      updateCountdown()

      interval = setInterval(() => {
        currentTime.value = Date.now()
        updateCountdown()
      }, 1000)
    })

    watch(() => props.outpostId, (id) => {
      if (id) subscribe(id)
    })

    watch([outpost, user, isAdmin], async () => {
      if (!outpost.value) return

      if (shouldSubscribePrivate()) {
        subscribePrivate(props.outpostId)
      } else {
        if (stopPrivate) {
          stopPrivate()
          stopPrivate = null
        }

        outpostPrivate.value = null
      }

      await loadDefenders('watch outpost/user/admin')
    })

    watch(outpostPrivate, async () => {
      await loadDefenders('watch private')
    })

    onBeforeUnmount(() => {
      if (stop) stop()
      if (stopPrivate) stopPrivate()

      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevBodyOverflow || ''

      if (interval) clearInterval(interval)
    })

    return {
      outpost,
      emitClose,

      defendersData,
      defendersTabName,
      handleImageError,
      getTotalStat,
      getDice,
      canRecall,
      recallVillager,
      resetRecallTime,

      isAssigning,
      selectedVillagerIds,
      playerVillagers,
      startAssign,
      cancelAssign,
      toggleSelect,
      assignVillagers,

      isAdmin,
      recalling,
      resetting,
      assigningLoading,
      assigningStarting,

      maxDefenders,
      defenderCount,
      maxAssignable,

      confirmResign,
      resignOutpost,
      resigning,
      resignMessage,

      isNameAlreadySelected,
      isNameInCurrentDefenders,
      isSelectable,
      sortedPlayerVillagers,

      // Treasure
      openTreasureBox,
      isOpeningTreasure,
      confirmOpen,
      showHintOpen,
      treasureMessage,
      countdownText,
      remainingTreasureMs,
      treasureOpened,
      isTreasureAvailable,
      formatRemaining,
      isOwnOutpost,

      // Attack readiness
      remainingMs,
      resetCooldown,

      // Villager modal
      selectedVillager,
      showPowerModal,
      openPowerModal,
      showUpgradeModal,
      openUpgradeModal,
      handleUpdated,

      gameVillagersData
    }
  }
}
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
@keyframes modalFade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-modalFade {
  animation: modalFade 0.25s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0.97);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scaleIn {
  animation: scaleIn 0.25s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.25s ease-out;
}

/* Utility tweaks */
.text-xxs {
  font-size: 0.68rem;
  line-height: 1rem;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Shake animation */
@keyframes shake {
  0% {
    transform: translateX(0);
  }

  10% {
    transform: translateX(-5px) rotate(-5deg);
  }

  20% {
    transform: translateX(5px) rotate(5deg);
  }

  30% {
    transform: translateX(-5px) rotate(-5deg);
  }

  40% {
    transform: translateX(5px) rotate(5deg);
  }

  50% {
    transform: translateX(-5px) rotate(-5deg);
  }

  60% {
    transform: translateX(5px) rotate(5deg);
  }

  70% {
    transform: translateX(-5px) rotate(-5deg);
  }

  80% {
    transform: translateX(5px) rotate(5deg);
  }

  90% {
    transform: translateX(-5px) rotate(-5deg);
  }

  100% {
    transform: translateX(0);
  }
}

.animate-shake {
  animation: shake 2.5s ease-in-out infinite;
}
</style>